import { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { X, Upload, Camera, RotateCw, Loader2, Trash2, ImageOff } from "lucide-react";

// ---- crop / resize helpers (canvas) ----
function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.setAttribute("crossOrigin", "anonymous");
    img.src = url;
  });
}

function getRadianAngle(deg) {
  return (deg * Math.PI) / 180;
}

function rotateSize(width, height, rotation) {
  const r = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(r) * width) + Math.abs(Math.sin(r) * height),
    height: Math.abs(Math.sin(r) * width) + Math.abs(Math.cos(r) * height),
  };
}

// Applies rotation, extracts the 1:1 crop, then downscales to <= maxSize.
// Modern browsers auto-apply EXIF orientation when drawing an <img>, so the
// only rotation we add is the user's explicit 90º steps.
async function getCroppedImage(src, pixelCrop, rotation = 0, maxSize = 800) {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const { width: bBoxW, height: bBoxH } = rotateSize(image.width, image.height, rotation);
  canvas.width = bBoxW;
  canvas.height = bBoxH;
  ctx.translate(bBoxW / 2, bBoxH / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const cropW = Math.round(pixelCrop.width);
  const cropH = Math.round(pixelCrop.height);
  const data = ctx.getImageData(Math.round(pixelCrop.x), Math.round(pixelCrop.y), cropW, cropH);

  const tmp = document.createElement("canvas");
  tmp.width = cropW;
  tmp.height = cropH;
  tmp.getContext("2d").putImageData(data, 0, 0);

  const size = Math.min(maxSize, cropW);
  const out = document.createElement("canvas");
  out.width = size;
  out.height = size;
  const octx = out.getContext("2d");
  octx.imageSmoothingQuality = "high";
  octx.drawImage(tmp, 0, 0, cropW, cropH, 0, 0, size, size);
  return out.toDataURL("image/jpeg", 0.85);
}

const isTouchDevice = () =>
  typeof window !== "undefined" &&
  (navigator.maxTouchPoints > 0 || window.matchMedia?.("(pointer: coarse)").matches);

export default function PhotoEditorModal({ open, initialPhoto = "", onClose, onSave, onRemove }) {
  const [imageSrc, setImageSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    if (open) {
      // start with an empty editing canvas; keep the existing photo as a preview only
      setImageSrc("");
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setSaving(false);
    }
  }, [open]);

  const onCropComplete = useCallback((_area, areaPixels) => setCroppedAreaPixels(areaPixels), []);

  const readFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Escolhe um ficheiro de imagem.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(String(reader.result));
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      toast.error("Seleciona e ajusta uma imagem primeiro.");
      return;
    }
    setSaving(true);
    try {
      const dataUrl = await getCroppedImage(imageSrc, croppedAreaPixels, rotation, 800);
      onSave?.(dataUrl);
      toast.success("Fotografia atualizada.");
      onClose?.();
    } catch (e) {
      toast.error("Não foi possível processar a imagem.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = () => {
    onRemove?.();
    toast.success("Fotografia removida.");
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="photo-editor-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
          <h2 className="font-display text-lg font-bold text-zinc-950">Fotografia do promotor</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100 text-zinc-500" data-testid="photo-editor-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {!imageSrc ? (
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                {initialPhoto ? (
                  <img src={initialPhoto} alt="" className="w-20 h-20 rounded-full object-cover bg-zinc-100 border border-zinc-200" data-testid="photo-editor-current" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400">
                    <ImageOff className="w-6 h-6" />
                  </div>
                )}
                <div className="text-sm text-zinc-500">
                  {initialPhoto ? "Substitui ou remove a fotografia atual." : "Ainda sem fotografia. Escolhe um ficheiro ou tira uma foto."}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="photo-editor-select-btn"
                  className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <Upload className="w-4 h-4" /> Selecionar ficheiro
                </button>
                {isTouchDevice() && (
                  <button
                    type="button"
                    onClick={() => cameraRef.current?.click()}
                    data-testid="photo-editor-camera-btn"
                    className="inline-flex items-center gap-2 border border-zinc-200 hover:bg-zinc-50 px-4 py-2.5 rounded-lg text-sm font-semibold text-zinc-800"
                  >
                    <Camera className="w-4 h-4" /> Tirar fotografia
                  </button>
                )}
              </div>

              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="photo-editor-file-input" onChange={(e) => readFile(e.target.files?.[0])} />
              <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" data-testid="photo-editor-camera-input" onChange={(e) => readFile(e.target.files?.[0])} />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative w-full h-72 bg-zinc-900 rounded-xl overflow-hidden" data-testid="photo-editor-crop-area">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                />
              </div>

              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-zinc-500 uppercase tracking-wide w-12">Zoom</span>
                <input
                  type="range"
                  min={1}
                  max={4}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  data-testid="photo-editor-zoom"
                  className="flex-1 accent-zinc-900"
                />
                <button
                  type="button"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  data-testid="photo-editor-rotate"
                  className="inline-flex items-center gap-1.5 border border-zinc-200 hover:bg-zinc-50 px-3 py-2 rounded-lg text-sm font-semibold text-zinc-800"
                  title="Rodar 90º"
                >
                  <RotateCw className="w-4 h-4" /> Rodar
                </button>
              </div>

              <button
                type="button"
                onClick={() => { setImageSrc(""); }}
                data-testid="photo-editor-choose-another"
                className="text-xs text-zinc-500 hover:text-zinc-900 hover:underline"
              >
                Escolher outra imagem
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-t border-zinc-100">
          <button
            type="button"
            onClick={handleSave}
            disabled={!imageSrc || saving}
            data-testid="photo-editor-save"
            className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar fotografia
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="photo-editor-cancel"
            className="px-5 py-2.5 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50"
          >
            Cancelar
          </button>
          {initialPhoto && (
            <button
              type="button"
              onClick={handleRemove}
              data-testid="photo-editor-remove"
              className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 px-3 py-2 rounded-lg"
            >
              <Trash2 className="w-4 h-4" /> Remover fotografia
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
